/**
 * Contrato interno — Comparáveis Valuation Enterprise (B2 RQ-028B).
 * Sem dados fictícios. Áreas: até 200% = ratio 2.0.
 */

import { AREA_FILTER_OPTIONS, RADIUS_STEP_METERS, RADIUS_SUGGESTIONS_M } from "./valuationAutomaticContract_v1";

export const COMPARABLES_STEP_STATUS = "integrated_search";

/** Modos API canónicos ↔ ids FE */
export const AREA_FILTER_MODE_BY_ID = Object.freeze({
  none: "NONE",
  m50_p200: "AREA_50_TO_200_PERCENT",
  pm25: "AREA_PLUS_MINUS_25",
  pm50: "AREA_PLUS_MINUS_50"
});

export const COMPARABLE_FIELD_KEYS = Object.freeze([
  "id",
  "endereco",
  "latitude",
  "longitude",
  "area",
  "quartos",
  "banheiros",
  "vagas",
  "valor",
  "valorM2",
  "distanciaM",
  "score",
  "origem",
  "fotoUrl",
  "link"
]);

export function createEmptyComparable() {
  return {
    id: null,
    endereco: "",
    latitude: null,
    longitude: null,
    area: null,
    quartos: null,
    banheiros: null,
    vagas: null,
    valor: null,
    valorM2: null,
    distanciaM: null,
    score: null,
    origem: "",
    fotoUrl: "",
    link: "",
    included: true,
    discard_reason: null
  };
}

export function createEmptyComparablesState() {
  return {
    status: COMPARABLES_STEP_STATUS,
    loading: false,
    error: null,
    subjectCoords: {
      latitude: null,
      longitude: null
    },
    filters: {
      radiusM: 500,
      // RQ-058B: alinhar default com auto-search (NONE) para não divergir ao «Buscar»
      areaFilterId: "none",
      requireFullAddress: false,
      limit: 80,
      offset: 0
    },
    items: [],
    discarded: [],
    discardCounts: {},
    selectedIds: [],
    excludedIds: [],
    selectionReasons: {},
    summary: {
      total: 0,
      included: 0,
      excluded: 0,
      avgValorM2: null,
      minDistanciaM: null,
      maxDistanciaM: null,
      eligibleTotal: 0,
      discardedTotal: 0
    },
    map: {
      ready: false,
      centerLatitude: null,
      centerLongitude: null,
      zoom: null,
      bounds: null
    },
    meta: {
      provider: "properties_readonly",
      fetchedAt: null,
      queryVersion: null,
      metrics: null,
      warnings: [],
      areaBounds: null
    }
  };
}

export const COMPARABLES_UI_REFS = Object.freeze({
  areaFilterOptions: AREA_FILTER_OPTIONS,
  radiusStepMeters: RADIUS_STEP_METERS,
  radiusSuggestionsM: RADIUS_SUGGESTIONS_M
});

export const COMPARABLES_API_PATHS = Object.freeze({
  search: "/api/v1/valuation-automatic/comparables/search",
  byId: "/api/v1/valuation-automatic/comparables/{id}",
  includeExclude: "/api/v1/valuation-automatic/comparables/selection"
});

function finiteNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Normaliza campos canónicos + legado para renderização (tabela/cards). */
export function resolveComparableDisplayFields(item = {}) {
  const valor = finiteNum(item.valor) ?? finiteNum(item.preco) ?? finiteNum(item.price);
  const area = finiteNum(item.area) ?? finiteNum(item.area_privativa);
  const valorM2 =
    finiteNum(item.valorM2) ??
    finiteNum(item.valor_unitario) ??
    finiteNum(item.vu) ??
    (valor != null && area != null && area > 0 ? valor / area : null);
  return {
    valor,
    valorM2,
    area,
    quartos: item.quartos ?? item.dormitorios ?? null,
    vagas: item.vagas ?? null,
    distanciaM: finiteNum(item.distanciaM) ?? finiteNum(item.distance_m),
    endereco: item.endereco || item.address || ""
  };
}

export function formatComparableCurrency(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0
  });
}

export function formatComparableNumber(value, digits = 0) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  });
}

export function computeSummary(items, selectedIds, excludedIds) {
  const list = Array.isArray(items) ? items : [];
  const excluded = new Set((excludedIds || []).map(Number));
  const selected = new Set((selectedIds || []).map(Number));
  const active = list.filter((it) => !excluded.has(Number(it.id)));
  const included = selected.size
    ? active.filter((it) => selected.has(Number(it.id)))
    : active;
  const vus = included.map((i) => Number(i.valorM2)).filter((n) => Number.isFinite(n) && n > 0);
  const dists = included.map((i) => Number(i.distanciaM)).filter((n) => Number.isFinite(n));
  return {
    total: list.length,
    included: included.length,
    excluded: excluded.size,
    avgValorM2: vus.length ? Math.round(vus.reduce((a, b) => a + b, 0) / vus.length) : null,
    minDistanciaM: dists.length ? Math.min(...dists) : null,
    maxDistanciaM: dists.length ? Math.max(...dists) : null
  };
}
